/**
 * Client ID management utility
 * Generates and manages a unique, collision-safe client ID stored in localStorage
 */

const CLIENT_ID_KEY = 'study-buddy-client-id';

/**
 * Generates a cryptographically secure random client ID
 * Uses crypto.randomUUID() for collision safety
 */
function generateClientId(): string {
  // Use crypto.randomUUID() if available (modern browsers)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback for older browsers - generate a UUID v4-like string
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Gets or creates a client ID from localStorage
 * Returns the same ID across browser sessions for consistency
 */
export function getClientId(): string {
  try {
    // Try to get existing ID from localStorage
    let clientId = localStorage.getItem(CLIENT_ID_KEY);

    if (!clientId) {
      // Generate new ID if none exists
      clientId = generateClientId();
      localStorage.setItem(CLIENT_ID_KEY, clientId);
    }

    return clientId;
  } catch (error) {
    // Fallback if localStorage is not available (e.g., private browsing)
    console.warn('localStorage not available, using session-only client ID: ', error);

    // Store in memory for the session
    if (!(window as any).__studyBuddyClientId) {
      (window as any).__studyBuddyClientId = generateClientId();
    }

    return (window as any).__studyBuddyClientId;
  }
}

/**
 * Regenerates the client ID (useful for testing or reset functionality)
 */
export function regenerateClientId(): string {
  try {
    const newId = generateClientId();
    localStorage.setItem(CLIENT_ID_KEY, newId);
    return newId;
  } catch (error) {
    console.warn('localStorage not available, regenerating session-only client ID: ', error);
    const newId = generateClientId();
    (window as any).__studyBuddyClientId = newId;
    return newId;
  }
}